import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTheme, Typography, useMediaQuery, Box } from '@mui/material'
import { Group } from '@mui/icons-material'
import { RootState } from '../../store'
import { useServiceRequest } from '../../hooks/useServiceRequest'
import { Lease, LeaseStatusType } from '../../types/lease'
import { useAppDispatch } from '../../hooks'
import { fetchLeases, setSelectedLease, setSelectedStatus } from '../../store/Lease/action'
import { fetchLeasingContext, setLeaseId } from '../../store/User/action'
import { FlexDiv, LeaseStatusBadges } from '../../components'
import { MoveInInspectionCard } from '../../components/MoveInInspectionCard/MoveInInspectionCard'
import { MoveOutCard } from '../../components/MoveOutCard/MoveOutCard'
import { NewDocumentsCard } from '../../components/NewDocumentsCard/NewDocumentsCard'
import { UpcomingInspectionCard } from '../../components/UpcomingInspectionCard/UpcomingInspectionCard'
import { ResponsivePageContainer } from '../../components/PageContainers'
import { LeaseStatus } from '../../types/lease'
import { PayNowCard } from '../../components/Payments/PayNowCard'
import { LeaseSummary } from './LeaseSummary'
import { MaintenanceSummary } from './MaintenanceSummary'
import { useInspectionHub } from '../../hooks/useInspectionHub'
import { useDocuments } from '../../hooks/useDocuments'
import { useLeaseOffers } from '../../hooks/useLeaseOffers'
import { LeaseOfferCard } from '../../components/LeaseOfferCard/LeaseOfferCard'
import { ApplicantWorkbook } from '../Applicant/ApplicantWorkbook'
import { ApplicantActionCard } from '../../components/ApplicantProcessCard/ApplicantActionCard'
import { ApplicantProcessCard } from '../../components/ApplicantProcessCard/ApplicantProcessCard'
import { AppRoutes } from '../../helpers/Routes'



export const Home = () => {
    const theme = useTheme()
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    // Data and state management
    const selectedStatus = useSelector(
        (state: RootState) => state.lease.selectedStatus,
    )
    const leaseList = useSelector((state: RootState) => state.lease.leaseList)
    const selectedLease = useSelector((state: RootState) => state.lease.selectedLease)
    const leaseId = selectedLease?.id
    const apartmentId = selectedLease?.apartment_id
    const leasingContext = useSelector(
        (state: RootState) => state.user.leasingContext,
    )

    const { hubData, getInspectionHub } = useInspectionHub(leaseId, apartmentId)
    const {
        documentList,
        getDocumentList,
        markDocumentAsViewed,
    } = useDocuments(apartmentId)
    const {
        offers,
        actingId,
        getOffers,
        acceptOffer,
        declineOffer,
        getTransferOptions,
        requestTransfer,
    } = useLeaseOffers()

    const {
        serviceRequestList,
        getServiceRequestList,
        getServiceRequestChannel,
    } = useServiceRequest()

    // Effects
    useEffect(() => {
        getServiceRequestList()
        dispatch(fetchLeases())
        dispatch(fetchLeasingContext())
        getOffers()
    }, [])

    const processStatus = leasingContext?.process_status ?? null
    const processCta = processStatus?.cta ?? null
    const showProcessAction =
        processCta != null &&
        processCta.kind !== 'review_offer' &&
        Boolean(processCta.label)

    const refreshAfterOffer = async (action: () => Promise<void>) => {
        await action()
        dispatch(fetchLeasingContext())
        getOffers()
    }

    const handleProcessAction = () => {
        if (!processCta) return
        if (processCta.href) {
            window.open(processCta.href, '_blank', 'noopener,noreferrer')
            return
        }
        if (processCta.route === 'documents') {
            navigate(AppRoutes.documents)
        }
    }

    useEffect(() => {
        if (leaseId != null && apartmentId != null) {
            getInspectionHub()
        }
    }, [leaseId, apartmentId])

    useEffect(() => {
        getDocumentList()
    }, [apartmentId])

    const unviewedDocuments = documentList.filter((doc) => !doc.tenant_viewed)
    const upcomingInspections = hubData?.property_inspections.upcoming ?? []

    const moveInInspection = selectedLease?.tenant_move_in_inspection
    const moveInRequiresAction =
        hubData?.move_in.state === 'action_required' ||
        moveInInspection?.status === 'READY' ||
        moveInInspection?.status === 'OVERDUE'

    const handleViewDocument = (doc: typeof documentList[number]) => {
        if (!doc.tenant_viewed) {
            markDocumentAsViewed(doc.id)
        }
        if (doc.document) {
            window.open(doc.document, '_blank')
        }
    }

    const handleLeaseSelect = (lease: Lease) => {
        dispatch(setSelectedLease(lease))
        dispatch(setSelectedStatus(lease.status))
    }

    const handleInspectionClick = (leaseId: number) => {
        dispatch(setLeaseId(leaseId))
        navigate(`../select-area/${leaseId}`)
    }

    const showMoveOut =
        selectedLease?.final_move_out_status != null ||
        selectedLease?.status === LeaseStatus.NOTICE ||
        selectedLease?.status === LeaseStatus.PAST

    const lifestyleLeadId = leasingContext?.active_lead_id
    const showLifestyleCard = Boolean(
        lifestyleLeadId &&
            (leasingContext?.workbook_remainder_open ||
                leasingContext?.workbook_lifestyle_editable),
    )

    const moveInCard = moveInInspection && selectedLease ? (
        <MoveInInspectionCard
            inspection={moveInInspection}
            leaseId={selectedLease.id}
            onInspectionClick={handleInspectionClick}
        />
    ) : null

    return (
        <ResponsivePageContainer>
            <FlexDiv
                direction="column"
                style={{
                    maxWidth: '700px',
                    width: '100%',
                }}
            >
                {/* Header */}
                <FlexDiv
                    style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: theme.spacing(3),
                    }}
                >
                    <div>
                        <Typography
                            variant={isMobile ? "h5" : "h4"}
                            style={{
                                color: '#fff',
                                marginBottom: theme.spacing(1),
                            }}
                        >
                            Welcome Home
                        </Typography>
                    </div>
                    {leaseList && (
                        <LeaseStatusBadges
                            leases={leaseList}
                            selectedLease={selectedLease || null}
                            onLeaseSelect={handleLeaseSelect}
                        />
                    )}
                </FlexDiv>

                {processStatus && (
                    <ApplicantProcessCard status={processStatus} />
                )}
                {showProcessAction && processCta && (
                    <ApplicantActionCard
                        cta={processCta}
                        onClick={handleProcessAction}
                        leaseTerms={processStatus?.lease_terms}
                    />
                )}

                {offers.map((offer) => (
                    <LeaseOfferCard
                        key={offer.id}
                        offer={offer}
                        acting={actingId === offer.id}
                        onAccept={(id) => refreshAfterOffer(() => acceptOffer(id))}
                        onDecline={(id, reason) =>
                            refreshAfterOffer(() => declineOffer(id, reason))
                        }
                        onLoadTransferOptions={getTransferOptions}
                        onTransfer={requestTransfer}
                    />
                ))}

                {showLifestyleCard && lifestyleLeadId && (
                    <Box
                        sx={{
                            background: 'linear-gradient(135deg, #2a2f35 0%, #1e2328 100%)',
                            borderRadius: 3,
                            padding: 3,
                            marginBottom: 3,
                            border: `1px solid ${theme.palette.grey[700]}`,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1,
                            }}
                        >
                            <Group style={{ color: '#46b083' }} />
                            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                                Roommate lifestyle profile
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#8b949e', mb: 3 }}>
                            {leasingContext?.workbook_progress?.lifestyle_complete
                                ? 'You can update these answers until your bed is confirmed.'
                                : 'Answer these lifestyle questions so we can suggest compatible roommates.'}
                        </Typography>
                        <ApplicantWorkbook
                            leadId={lifestyleLeadId}
                            remainderOnly
                        />
                    </Box>
                )}

                {/* Lease Summary Card */}
                <LeaseSummary />

                {/* Move-In Inspection — top priority when action is required */}
                {moveInRequiresAction && moveInCard}

                {/* New shared documents */}
                <NewDocumentsCard
                    documents={unviewedDocuments}
                    onViewDocument={handleViewDocument}
                />

                {/* Upcoming property inspections */}
                {upcomingInspections.length > 0 && (
                    <UpcomingInspectionCard upcoming={upcomingInspections} />
                )}

                {/* Move-In Inspection — lower priority when complete or pending */}
                {!moveInRequiresAction && moveInCard}

                {/* Move-Out Card */}
                {showMoveOut && selectedLease && (
                    <MoveOutCard
                        status={
                            hubData?.move_out.final_move_out_status ??
                            selectedLease.final_move_out_status ??
                            'not_started'
                        }
                        endDate={selectedLease.end_date}
                        leaseId={selectedLease.id}
                        moveOutState={hubData?.move_out.state}
                        fmoAvailable={hubData?.move_out.fmo_available ?? false}
                    />
                )}

                {/* Payment Due */}
                {selectedLease?.allow_rent && <PayNowCard />}

                {/* Maintenance */}
                <MaintenanceSummary />
            </FlexDiv>
        </ResponsivePageContainer>
    )
}
